import { isProductSlug, products } from "@/content/product"
import { isSolutionSlug, solutions } from "@/content/solutions"
import { SITE_DEFAULT_DESCRIPTION, SITE_DEFAULT_OG_IMAGE, SITE_URL } from "@/lib/site"

export { SITE_URL }

const HOME_TITLE = "Sqaleshop: The sales link for your business"

function withBrand(pageTitle: string): string {
  return `${pageTitle} | Sqaleshop`
}

export type ResolvedSeo = {
  title: string
  description: string
  ogImage: string
}

function absoluteAssetUrl(url: string): string {
  if (url.startsWith("http://") || url.startsWith("https://")) return url
  if (url.startsWith("//")) return `https:${url}`
  return `${SITE_URL}${url.startsWith("/") ? url : `/${url}`}`
}

export function resolveSeo(pathname: string, slug?: string): ResolvedSeo {
  const path = pathname.replace(/\/+$/, "") || "/"

  if (path === "/") {
    return {
      title: HOME_TITLE,
      description: SITE_DEFAULT_DESCRIPTION,
      ogImage: SITE_DEFAULT_OG_IMAGE,
    }
  }

  if (path.startsWith("/solutions/") && slug && isSolutionSlug(slug)) {
    const s = solutions[slug]
    return {
      title: withBrand(s.title),
      description: s.intro,
      ogImage: absoluteAssetUrl(s.image),
    }
  }

  if (path.startsWith("/product/") && slug && isProductSlug(slug)) {
    const p = products[slug]
    return {
      title: withBrand(p.title),
      description: p.intro,
      ogImage: absoluteAssetUrl(p.image),
    }
  }

  const staticMap: Record<string, { title: string; description: string }> = {
    "/features": {
      title: withBrand("Everything you need to sell online"),
      description:
        "Product management, orders, inventory, and checkout tools built for small businesses who sell on social and beyond.",
    },
    "/product": {
      title: withBrand("Everything under your sales link"),
      description:
        "Your sales link, checkout, insight, and customer tools, built as one product you share with a single URL.",
    },
    "/pricing": {
      title: withBrand("Start free. Grow when you are ready."),
      description:
        "Transparent pricing for Sqaleshop. Start on the free tier and upgrade when you need more capacity and features.",
    },
    "/company": {
      title: withBrand("Calm software for entrepreneurs who sell everywhere"),
      description:
        "Learn about Sqaleup Inc. and why we build Sqaleshop for real-world sellers—not generic enterprise playbooks.",
    },
    "/contact": {
      title: withBrand("Let's talk about your store"),
      description:
        "Sales, partnerships, press, or product questions? Reach Sqaleup Inc. For account issues, use in-app support.",
    },
    "/solutions": {
      title: withBrand("Built for how you actually sell"),
      description:
        "Sqaleshop is a simple, modern commerce platform for small businesses who sell on social, especially those who started in DMs.",
    },
    "/careers": {
      title: withBrand("Help sellers grow without the stress"),
      description:
        "Join the team building calm, practical tools for entrepreneurs. See open roles and how we work at Sqaleshop.",
    },
    "/team": {
      title: withBrand("People behind Sqaleshop"),
      description: "Meet the people building Sqaleshop and the values that guide the product.",
    },
    "/compliance": {
      title: withBrand("Trust, clarity, and responsibility"),
      description:
        "How Sqaleshop approaches security, merchant responsibilities, and compliance-minded practices.",
    },
    "/terms": {
      title: withBrand("Terms & Conditions"),
      description: "Terms governing use of the Sqaleshop marketing site and related services.",
    },
    "/privacy": {
      title: withBrand("Privacy policy"),
      description: "How Sqaleup Inc. collects, uses, and protects information when you use Sqaleshop.",
    },
    "/merchant-agreement": {
      title: withBrand("Merchant agreement"),
      description: "Commercial terms between merchants and Sqaleup Inc. for use of the Sqaleshop platform.",
    },
  }

  const match = staticMap[path]
  if (match) {
    return { title: match.title, description: match.description, ogImage: SITE_DEFAULT_OG_IMAGE }
  }

  return {
    title: HOME_TITLE,
    description: SITE_DEFAULT_DESCRIPTION,
    ogImage: SITE_DEFAULT_OG_IMAGE,
  }
}

export function canonicalUrl(pathname: string): string {
  const path = pathname.replace(/\/+$/, "") || "/"
  if (path === "/") {
    return `${SITE_URL}/`
  }
  return `${SITE_URL}${path}`
}
